import React, { useState, useEffect } from 'react';
import { Book, CheckCircle, AlertCircle, FileText, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { FIREBASE_DB, FIREBASE_AUTH } from '../firebase.config';
import { collection, addDoc, query, where, getDocs, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { Navigate } from 'react-router-dom';

const TrainingPage = () => {
  const [expandedSections, setExpandedSections] = useState({
    welcome: true,
    expectations: false,
    performance: false,
    customerService: false,
    companyPolicies: false,
    healthAndSafety: false,
    workplaceConduct: false,
    disciplineAndPerformance: false,
    attendanceAndPunctuality: false,
    communicationGuidelines: false,
    confidentiality: false,
    employeeBenefits: false,
    timesheets: false,
    personalLeave: false,
    dressCode: false
  });

  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasExistingAcknowledgment, setHasExistingAcknowledgment] = useState(false);
  const [user, setUser] = useState(null);

  // Check for existing acknowledgment
  useEffect(() => {
    const checkForExistingAcknowledgment = async () => {
      try {
        setLoading(true);
        const currentUser = FIREBASE_AUTH.currentUser;
        
        if (!currentUser) {
          // Not logged in, redirect to login
          setLoading(false);
          return;
        }
        
        setUser(currentUser);
        
        // Check if user has already completed the training
        const acknowledgementsRef = collection(FIREBASE_DB, 'trainingAcknowledgments');
        const q = query(acknowledgementsRef, where('userId', '==', currentUser.uid), where('trainingType', '==', 'onboarding'));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          setHasExistingAcknowledgment(true);
        }
      } catch (err) {
        console.error('Error checking for existing acknowledgment:', err);
        setError('Failed to verify training status. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    checkForExistingAcknowledgment();
  }, []);

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  const handleAcknowledge = () => {
    setAcknowledged(!acknowledged);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      if (!user) {
        throw new Error('You must be logged in to submit the acknowledgment');
      }
      // Fetch Firestore user profile
      let displayName = user.displayName || 'Unknown';
      try {
        const userDocRef = doc(FIREBASE_DB, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          if (userData.name) displayName = userData.name;
        }
      } catch (err) {
        // If fetching Firestore user fails, fallback to displayName from Auth
      }
      // Create acknowledgment document in Firestore
      await addDoc(collection(FIREBASE_DB, 'trainingAcknowledgments'), {
        userId: user.uid,
        userEmail: user.email,
        displayName,
        trainingType: 'onboarding',
        acknowledgedAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        sections: Object.keys(expandedSections).filter(key => expandedSections[key]),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error saving acknowledgment:', err);
      setError('Failed to save your acknowledgment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderSection = (key, title, content) => {
    const isExpanded = expandedSections[key];
    
    return (
      <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection(key)}
          className="w-full flex justify-between items-center p-4 bg-gradient-to-r from-green-50 to-white text-left"
          aria-expanded={isExpanded}
          aria-controls={`section-${key}`}
        >
          <h2 className="text-lg sm:text-xl font-semibold text-green-800 break-words pr-2">{title}</h2>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 flex-shrink-0 text-green-600" />
          ) : (
            <ChevronDown className="h-5 w-5 flex-shrink-0 text-green-600" />
          )}
        </button>
        
        {isExpanded && (
          <div id={`section-${key}`} className="p-3 sm:p-4 bg-white">
            {content}
          </div>
        )}
      </div>
    );
  };

  // Redirect if not logged in
  if (!loading && !user) {
    return <Navigate to="/login" state={{ from: '/training' }} />;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center px-4">
        <div className="text-center">
          <Loader className="h-10 w-10 sm:h-12 sm:w-12 animate-spin text-green-600 mx-auto mb-3 sm:mb-4" />
          <p className="text-base sm:text-lg text-gray-700">Loading training information...</p>
        </div>
      </div>
    );
  }

  // If user has already acknowledged the training
  if (hasExistingAcknowledgment) {
    return (
      <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-5 sm:p-8 rounded-lg shadow-md text-center">
            <CheckCircle className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-green-500 mb-3 sm:mb-4" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              Training Already Completed
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6">
              You have already completed and acknowledged the SBG onboarding training. Thank you for your commitment to our policies and procedures.
            </p>
            <a
              href="/employee-portal"
              className="inline-block bg-green-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-md text-sm sm:text-base font-medium hover:bg-green-700 transition-colors"
            >
              Return to Employee Portal
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-5 sm:p-8 rounded-lg shadow-md text-center">
            <CheckCircle className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-green-500 mb-3 sm:mb-4" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              Training Acknowledgment Submitted
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6">
              Thank you for completing the SBG onboarding training. Your acknowledgment has been recorded in our system.
            </p>
            <a
              href="/employee-portal"
              className="inline-block bg-green-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-md text-sm sm:text-base font-medium hover:bg-green-700 transition-colors"
            >
              Return to Employee Portal
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8 pb-12 sm:pb-16">
      <Helmet>
        <title>SBG Onboarding Training</title>
      </Helmet>
      
      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="text-center mb-6 sm:mb-8">
          <Book className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-green-600 mb-3 sm:mb-4" />
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            SBG Onboarding Training
          </h1>
          <p className="text-lg sm:text-xl text-gray-600">
            Welcome to the Scott Business Group team
          </p>
        </div>
        
        <div className="bg-orange-50 border-l-4 border-orange-500 p-3 sm:p-4 mb-6 sm:mb-8 rounded-r-md">
          <div className="flex">
            <div className="flex-shrink-0 pt-1">
              <AlertCircle className="h-5 w-5 text-orange-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-base sm:text-lg font-medium text-orange-800">Disclaimers</h3>
              <div className="mt-1 sm:mt-2 text-sm sm:text-base text-orange-700">
                <p className="mb-2">1. This document pertains to the internal onboarding training for the Scott Business Group. If applicable, on-the-job training will be conducted at the designated job site.</p>
                <p>2. Please refer to the employee manual for more information and details on policies and procedures.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Welcome Section */}
        {renderSection(
          'welcome',
          'Welcome to SBG',
          <div className="prose max-w-none">
            <p>Welcome to SBG onboarding training! We are excited to have you join our team and embark on this journey together. This training will give you the essential knowledge and tools to thrive in your new role and contribute to your success.</p>
            <p>Throughout the onboarding process, you will gain a comprehensive understanding of SBG's culture, values, and operational practices.</p>
            <p>We aim to ensure you feel supported, informed, and equipped to tackle the challenges ahead. We encourage you to ask questions, engage with the material, and share your thoughts as we move through the training. Let's dive in and set the stage for a successful start!</p>
          </div>
        )}
        
        {/* Expectations */}
        {renderSection(
          'expectations',
          'Expectations',
          <div className="prose max-w-none">
            <ul>
              <li><strong>Dependability:</strong> Employees are expected to consistently be present and prepared to contribute to the team's goals.</li>
              <li><strong>Adherence to Policies and Procedures:</strong> Employees should familiarize themselves with and follow all established guidelines to ensure workplace compliance and proper functioning.</li>
              <li><strong>Follow-Through:</strong> Employees need to complete all job requirements as directed to ensure operational effectiveness.</li>
              <li><strong>Communication:</strong> Employees are encouraged to ask questions or seek clarification whenever necessary to ensure a clear understanding of directives.</li>
            </ul>
          </div>
        )}
        
        {/* Performance Outline */}
        {renderSection(
          'performance',
          'Performance Outline',
          <div className="prose max-w-none">
            <ul>
              <li><strong>Excellence:</strong> Employees are expected to strive for excellence in their roles, showcasing their best performance daily.</li>
              <li><strong>Professionalism and Integrity:</strong> Representing the company with professionalism and integrity in all interactions is crucial.</li>
              <li><strong>Continuous Improvement:</strong> Employees should look for opportunities to enhance their skills and contribute to the team's overall improvement.</li>
            </ul>
          </div>
        )}
        
        {/* Customer Service */}
        {renderSection(
          'customerService',
          'Customer Service',
          <div className="prose max-w-none">
            <p>At SBG, we're all about our customers! We believe that every interaction is a chance to build lasting relationships, and that's why we strongly emphasize treating everyone with professionalism and kindness. This level of service is expected from all our associates. When our customers reach out, they can expect not just our expertise but a warm and welcoming experience that makes them feel valued. After all, those meaningful connections keep us striving for excellence every day!</p>
            
            <h3 className="text-lg font-medium mt-4">Responsiveness</h3>
            <p>Employees are expected to respond promptly to clients' inquiries and concerns, reinforcing a commitment to excellent service.</p>
            
            <h3 className="text-lg font-medium mt-4">Internal Customer Commitment</h3>
            <p>Co-workers should be treated with the same respect and attention as external clients, fostering a collaborative work environment.</p>
          </div>
        )}
        
        {/* Company Policies */}
        {renderSection(
          'companyPolicies',
          'Company Policies',
          <div className="prose max-w-none">
            <p>The company strictly adheres to all state and federal laws.</p>
            
            <h3 className="text-lg font-medium mt-4">Assigned Resources</h3>
            <h4 className="font-medium">Computer and Internet Usage:</h4>
            <p>Employees are required to use client-owned computers and network resources responsibly. Policies prohibit unauthorized access to software and other individuals' computers.</p>
            
            <h4 className="font-medium mt-4">Mobile Phones</h4>
            <p><strong>Work Provided Mobile Phone:</strong> Employees provided with a work mobile phone must use it primarily for business purposes. Personal use should be kept to a minimum and not interfere with work responsibilities. Employees are responsible for the care and maintenance of the company-issued devices.</p>
            <p><strong>Security and Privacy:</strong> Employees must protect sensitive company information and personal data on all mobile devices. Sharing passwords or allowing unauthorized access to company mobile phones is prohibited. In the event of loss or theft of a work-provided mobile phone, employees must report it immediately.</p>
            
            <h4 className="font-medium mt-4">Personal Vehicles for Business Use:</h4>
            <p><strong>Personal Use Definition:</strong> Personal use refers to any instance an employee utilizes their vehicle for business purposes. This includes travel to meetings, off-site events, and commuting between different work locations.</p>
            
            <h4 className="font-medium mt-2">Responsibilities</h4>
            <p>Employees must:</p>
            <ul>
              <li>Have a clean driver's record.</li>
              <li>Ensure their vehicle operates safely and complies with all local regulations and safety standards.</li>
              <li>Maintain valid driver's licenses and vehicle insurance that meets the minimum requirements outlined by state laws.</li>
              <li>Keep accurate records of business-related travel, including dates, destinations, purpose of travel, and mileage.</li>
            </ul>
            
            <p><strong>Reimbursement Guidelines:</strong> Employees may be eligible for mileage reimbursement when using their vehicle for business purposes. The following conditions apply:</p>
            <ul>
              <li>Reimbursement will be provided at the current IRS standard mileage rate.</li>
              <li>Mileage must be documented and submitted through the designated expense reporting system.</li>
            </ul>
            
            <p><strong>Insurance Requirements:</strong> Employees must maintain a valid auto insurance policy that includes the following:</p>
            <ul>
              <li>Personal liability coverage</li>
              <li>Collision coverage</li>
              <li>Comprehensive coverage (recommended)</li>
              <li>Employees should provide proof of insurance to SBG HR upon request.</li>
            </ul>
            
            <p><strong>Accident Reporting:</strong> In the event of an accident while using a personal vehicle for business, employees must:</p>
            <ul>
              <li>Ensure safety first and provide medical assistance if necessary.</li>
              <li>Notify local authorities and obtain a police report.</li>
              <li>Inform your supervisor and the SBG HR department immediately and provide relevant documentation.</li>
            </ul>
            
            <p><strong>Compliance with Traffic Laws:</strong> Employees must obey all traffic laws and regulations while driving their vehicles for business purposes. Failure to do so may result in disciplinary action.</p>
          </div>
        )}
        
        {/* Health and Safety */}
        {renderSection(
          'healthAndSafety',
          'Health and Safety',
          <div className="prose max-w-none">
            <p>SBG has established a comprehensive safety policy to foster a healthy and secure workplace environment for all employees. This policy emphasizes strict adherence to safety regulations and best practices to prevent workplace injuries and promote overall well-being.</p>
            
            <h3 className="text-lg font-medium mt-4">Key components of the safety policy include:</h3>
            
            <p><strong>Hazard Identification and Reporting:</strong> A clear process is in place for identifying, reporting, and addressing potential hazards in the workplace. Employees are encouraged to report unsafe conditions immediately to their immediate manager and Scott Business Group representative.</p>
            
            <p><strong>PPE:</strong> Employees should wear protective equipment (PPE) according to their specific roles and responsibilities, ensuring safety measures are in place for all tasks.</p>
            
            <p><strong>Emergency Preparedness:</strong> Employees must adhere to all emergency preparedness plans for various scenarios defined by the specific clients.</p>
            
            <p><strong>Monitoring and Evaluation:</strong> The effectiveness of the safety policy will be continually assessed through regular audits, employee feedback, and incident analysis to identify areas for improvement.</p>
            
            <p><strong>Smoke-Free Environment:</strong> The organization enforces a strict smoke-free policy that reinforces its commitment to the health and well-being of all employees. This policy prohibits smoking in and around company premises, creating a clean and healthy environment that reduces the risk of secondhand smoke exposure and encourages a healthier lifestyle for everyone associated with the organization. By implementing these comprehensive measures, the organization aims to maintain a safe and supportive work environment that prioritizes the health and safety of its employees.</p>
            
            <p><strong>Reporting Accidents and Incidents:</strong> All accidents and incidents must be reported immediately. Please ensure that these occurrences are communicated promptly to the relevant supervisor and the Scott Business Group (SBG) within an immediate timeframe. Timely reporting is essential for maintaining a safe and compliant work environment. Please note that Workers' Compensation reporting is time-sensitive.</p>
          </div>
        )}
        
        {/* Workplace Conduct */}
        {renderSection(
          'workplaceConduct',
          'Workplace Conduct Policy',
          <div className="prose max-w-none">
            <p><strong>Professionalism:</strong> Employees are expected to maintain a high standard of professionalism in their interactions with colleagues and clients.</p>
            
            <p><strong>Adherence to Policies:</strong> Employees must comply with all company policies and procedures, including those related to harassment and discrimination.</p>
            
            <p><strong>Respect and Integrity:</strong> All team members should treat each other with respect and integrity, fostering a positive work environment.</p>
            
            <h3 className="text-lg font-medium mt-4">The following offenses shall result in disciplinary action, including employment termination:</h3>
            <ul>
              <li>Excessive tardiness or excessive absenteeism</li>
              <li>Horseplay or disorderly conduct, or the use of obscene language</li>
              <li>Failure to comply with the applicable dress code</li>
              <li>Unauthorized entry into restricted areas of SBG where the employee's work does not require their presence</li>
              <li>Interfering with the work of another employee</li>
              <li>Solicitation during working time, including soliciting for membership in organizations, asking for donations or contributions, selling tickets or merchandise, or similar acts</li>
              <li>Failure to report work-related injuries or accidents</li>
              <li>Leaving assigned work area or SBG without prior authorization, except during the lunch hour</li>
              <li>Failure to properly utilize timekeeping records/ procedures</li>
              <li>Smoking in unauthorized areas</li>
              <li>Disclosing confidential or proprietary information to unauthorized sources or permitting unauthorized sources access to confidential or proprietary information</li>
              <li>Intimidating, threatening, or coercing another employee through physical, verbal, or sexual harassment</li>
              <li>Cellular phones (including texting) are prohibited during working hours. They are only allowed during the lunch hour or break time.</li>
            </ul>
            
            <p><strong>Reporting Violations:</strong> Employees are encouraged to report violations of the conduct policy or unethical behavior to SBG Employee Relations Management or through designated channels.</p>
            
            <p><strong>Consequences:</strong> Violations of the conduct policy may lead to disciplinary actions, including termination.</p>
            
            <p>The conduct policy is designed to promote a respectful and productive workplace and ensure that all employees can effectively perform their roles.</p>
          </div>
        )}
        
        {/* Discipline and Performance */}
        {renderSection(
          'disciplineAndPerformance',
          'Discipline and Performance',
          <div className="prose max-w-none">
            <p>The company clearly outlines grounds for disciplinary action, which include violations of company policies, insubordination, and unethical behavior.</p>
            <p>Procedures for addressing performance issues involve regular reviews and constructive feedback. Employees are encouraged to recognize performance goals and the consequences of not meeting expectations.</p>
          </div>
        )}
        
        {/* Attendance and Punctuality */}
        {renderSection(
          'attendanceAndPunctuality',
          'Attendance and Punctuality',
          <div className="prose max-w-none">
            <p>Attendance and punctuality policies stress the importance of being present and on time to contribute effectively as a team member.</p>
            <p>Employees must notify management in advance when they are unable to attend work, and repeated tardiness or absenteeism may result in disciplinary measures.</p>
          </div>
        )}
        
        {/* Communication Guidelines */}
        {renderSection(
          'communicationGuidelines',
          'Communication Guidelines',
          <div className="prose max-w-none">
            <p><strong>Open Communication:</strong> We encourage transparent dialogue among team members to share ideas, feedback, and concerns, ensuring everyone feels heard and valued.</p>
            <p>Employees must adhere to specific protocols when making public statements or engaging on social media regarding the company to protect its reputation and confidentiality.</p>
            <p><strong>Communication with Human Resources:</strong> If you have any questions or need clarification regarding the contents of this training or any related policies, we encourage you to contact the SBG Human Resources department. They are available to assist you and can be contacted by telephone at (602) 400-8028.</p>
          </div>
        )}
        
        {/* Confidentiality and Privacy */}
        {renderSection(
          'confidentiality',
          'Confidentiality and Privacy',
          <div className="prose max-w-none">
            <p>Confidentiality policies mandate that employees must safeguard proprietary company information and protect the privacy of fellow employees. Unauthorized sharing of sensitive information could lead to serious disciplinary action.</p>
            <p>Employees should be careful when discussing workplace matters, always ensuring respect for privacy and confidentiality.</p>
          </div>
        )}
        
        {/* Employee Benefits */}
        {renderSection(
          'employeeBenefits',
          'Employee Benefits',
          <div className="prose max-w-none">
            <p>Health and dental insurance coverage will be made available to full-time employees after 60 days of employment, following company policy. At this time, you will receive the necessary enrollment information.</p>
            
            <p>As a full-time (40 hours per week) contract employee, you will become eligible for holiday pay upon completing 1,000 work hours (approximately 6 months of service). Scott Business Group observes six paid holidays, which include:</p>
            
            <ol>
              <li>New Year's Day</li>
              <li>Memorial Day</li>
              <li>Independence Day</li>
              <li>Labor Day</li>
              <li>Thanksgiving Day</li>
              <li>Christmas Day</li>
            </ol>
            
            <p>Additionally, contract employees are entitled to five (5) vacation days after accruing 2,000 service hours in a one-year period.</p>
            
            <p>Employees will earn one hour of Paid Sick Time for every 30 hours worked. Please refer to the policy for further details.</p>
          </div>
        )}
        
        {/* Timesheets */}
        {renderSection(
          'timesheets',
          'Timesheets',
          <div className="prose max-w-none">
            <p>Timesheets must be signed, approved, and submitted by noon on Mondays without exception. You are responsible for tracking your time accurately, reviewing it, and submitting it for approval through the Scott Business Group Attendance Web App at <a href="https://sbg-attendance.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://sbg-attendance.com</a>.</p>
            
            <p>Before your first submission, the SBG Payroll Representative will provide you with all necessary information and app credentials to ensure a smooth process.</p>
            
            <p>Please be aware that paychecks for late submissions will not be processed until the following pay cycle. Timely submission of timesheets is crucial—you must adhere to this deadline to avoid delays in your payment.</p>
            
            <p>These detailed policies are designed to foster a professional, safe, and respectful working environment in alignment with the organization's core values and mission, promoting excellence and accountability among all employees.</p>
          </div>
        )}
        
        {/* Personal Leave */}
        {renderSection(
          'personalLeave',
          'Personal Leave',
          <div className="prose max-w-none">
            <p>A Personal Leave Form is required for all types of work absences to ensure proper documentation and approval. These absences can include:</p>
            
            <ul>
              <li><strong>Personal Illness:</strong> When an employee is unable to work due to health-related issues.</li>
              <li><strong>Family Emergencies:</strong> Situations that require immediate attention, such as a family member's illness or urgent matters.</li>
              <li><strong>Scheduled Medical Appointments:</strong> Absences for routine check-ups or treatments that cannot be scheduled outside of work hours.</li>
              <li><strong>Bereavement Leave:</strong> Time off taken due to the death of a loved one.</li>
              <li><strong>Jury Duty:</strong> If you receive a summons to fulfill this civic responsibility.</li>
              <li><strong>Military Obligation:</strong> Reservist and National Guard.</li>
              <li><strong>Vacation:</strong> Must be approved by the immediate manager.</li>
            </ul>
            
            <p>Filling out the Personal Leave Form for any of these absences ensures clear communication and accountability in the workplace. Regardless of whether your leave is paid or unpaid, you must complete a form.</p>
          </div>
        )}
        
        {/* Job Assignment Dress Code */}
        {renderSection(
          'dressCode',
          'Job Assignment Dress Code',
          <div className="prose max-w-none">
            <p>At Scott Business Group, we believe that a professional appearance contributes to a positive work environment and reflects our commitment to excellence. The general dress code for most job assignments is business casual.</p>
            
            <h3 className="text-lg font-medium mt-4">Business Casual Guidelines:</h3>
            <ul>
              <li>Acceptable attire includes collared shirts, blouses, slacks, skirts, and dresses.</li>
              <li>Employees are encouraged to wear neat and clean clothing that is appropriate for a professional setting.</li>
            </ul>
            
            <h3 className="text-lg font-medium mt-4">Prohibited Attire:</h3>
            <ul>
              <li>Clothing with political messages, slogans, or graphics is not permitted.</li>
              <li>Attire that is deemed inappropriate or distracting, including but not limited to ripped jeans, overly casual wear, or provocative clothing, is not allowed.</li>
              <li>Flip Flops</li>
            </ul>
            
            <p>We expect all employees to adhere to this dress code and uphold professional standards in compliance with our client's work environment.</p>
            
            <h3 className="text-lg font-medium mt-4">General Grooming</h3>
            <p>Employees are expected to maintain a standard of personal grooming that reflects professionalism. This includes wearing clean clothing, maintaining personal cleanliness, and practicing good oral hygiene.</p>
          </div>
        )}
        
        {/* Acknowledgment Section */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mt-6 sm:mt-8 border-t-4 border-green-600">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
            <FileText className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-green-600" />
            Training Acknowledgment
          </h2>
          
          <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
            Please review all sections of this training document carefully. By checking the box below, you acknowledge that you have read and understood the content of this training, and you agree to comply with all policies and procedures outlined herein. You also understand that additional information can be found in the employee manual.
          </p>
          
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 mb-4 sm:mb-6 rounded-r-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="flex items-start">
              <div className="flex items-center h-5 mt-0.5">
                <input
                  id="acknowledgment"
                  name="acknowledgment"
                  type="checkbox"
                  checked={acknowledged}
                  onChange={handleAcknowledge}
                  className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  required
                />
              </div>
              <div className="ml-3 text-xs sm:text-sm">
                <label htmlFor="acknowledgment" className="font-medium text-gray-700">
                  I acknowledge that I have read and understood the SBG Onboarding Training. I understand that this training provides an overview and that more detailed information is available in the employee manual.
                </label>
              </div>
            </div>
            
            <div>
              <button
                type="submit"
                disabled={!acknowledged || submitting}
                className={`w-full flex justify-center py-2 sm:py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  !acknowledged ? 'bg-gray-400 cursor-not-allowed' : submitting ? 'bg-green-500' : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                }`}
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Acknowledgment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TrainingPage; 